import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, Reorder, motion } from 'motion/react'
import { Dices, RotateCcw } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { SECTION } from '@/components/dashboard/fx/layout'
import { EASE } from '@/components/dashboard/fx/motion'
import { usePlayerActions, usePlayerState } from '@/components/player/PlayerProvider'
import { AdventureDial } from '@/components/build/AdventureDial'
import { RideSummary } from '@/components/build/RideSummary'
import { SlotCard } from '@/components/build/SlotCard'
import { itemTrack, setlistText } from '@/components/build/types'
import { DISCIPLINE_TABS } from '@/components/dashboard/DisciplineDock'
import { usePlaylistBuild, type BuildParams } from '@/hooks/usePlaylistBuild'
import { usePlaylistOptions } from '@/hooks/usePlaylistOptions'
import { initials } from '@/lib/format'

const NOUN: Record<string, string> = { cycling: 'ride', running: 'run', walking: 'walk' }

interface BuilderBoardProps {
  params: BuildParams
  initial: { seed: number; adventure: number }
  // Called as the dial moves / the ride is re-rolled, so the URL stays a shareable recipe.
  onRecipeChange: (recipe: { seed: number; adventure: number }) => void
}

const MESSAGES = (name: string) => ['Digging through the crates…', `Checking what ${name} never plays…`, 'Finding a perfect opener…', 'Lining up the peak…']

// The wait for a first build (a second or two) is part of the show.
function Building({ name }: { name: string }) {
  const messages = useMemo(() => MESSAGES(name), [name])
  const [i, setI] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setI((n) => (n + 1) % messages.length), 1400)
    return () => clearInterval(timer)
  }, [messages.length])

  return (
    <div className="flex flex-col items-center gap-6 py-24 text-center">
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 2.4, ease: 'linear', repeat: Infinity }} className="size-16 rounded-full border-4 border-primary/20 border-t-primary" />
      <AnimatePresence mode="wait">
        <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3 }} className="font-display text-2xl font-semibold tracking-tight">
          {messages[i]}
        </motion.p>
      </AnimatePresence>
    </div>
  )
}

export function BuilderBoard({ params, initial, onRecipeChange }: BuilderBoardProps) {
  const build = usePlaylistBuild(params, initial)
  const { data: options } = usePlaylistOptions(params.instructorId)
  const player = usePlayerState()
  const { playQueue, close } = usePlayerActions()
  const [openId, setOpenId] = useState<string | null>(null)
  const [rolls, setRolls] = useState(0)

  const onRecipeRef = useRef(onRecipeChange)
  onRecipeRef.current = onRecipeChange
  useEffect(() => onRecipeRef.current({ seed: build.seed, adventure: build.adventure }), [build.seed, build.adventure])

  const name = options?.instructor.name ?? 'the instructor'
  const first = name.split(' ')[0]
  const noun = NOUN[params.discipline] ?? 'class'
  const disciplineLabel = DISCIPLINE_TABS.find((d) => d.value === params.discipline)?.label ?? params.discipline

  const { entries, frame } = build
  const withItems = entries.filter((e) => e.item)
  const queue = withItems.map((e) => itemTrack(e.item!))
  const queueIndexFor = (id: string) => entries.slice(0, entries.findIndex((e) => e.id === id)).filter((e) => e.item).length
  const auditioning = Boolean(player.queue && queue.length && player.queue.tracks[0]?.key === queue[0]?.key)

  const title = `${params.minutes}-minute ${params.theme ? `${params.theme} ` : ''}${noun}`
  const setlist = () => setlistText(`${name}'s ${title}`, entries.map((entry, i) => ({ item: entry.item, role: frame[i]?.role ?? '', startsAt: frame[i]?.startsAt ?? 0 })))
  const link = () => window.location.href

  return (
    <div className="overflow-x-clip">
      <PageHero
        back={{ to: '/build', label: 'Change the setup' }}
        compact
        eyebrow="Ride builder"
        title={`${first}'s`}
        accent={title}
        description={`${disciplineLabel} · ${build.meta ? `${build.meta.ridesConsidered} of ${first}'s matching rides studied · ` : ''}drag, lock, swap and audition until it's right`}
        media={
          <Avatar className="size-16 shrink-0 ring-2 ring-primary/40 ring-offset-4 ring-offset-background sm:size-20">
            <AvatarImage src={options?.instructor.image_url ?? undefined} alt="" />
            <AvatarFallback className="font-display text-2xl">{initials(name)}</AvatarFallback>
          </Avatar>
        }
      />

      <section className={`${SECTION} !py-10 sm:!py-14`}>
        {build.status === 'error' && (
          <div className="rounded-3xl border border-destructive/40 p-8 text-center">
            <p className="mb-4 text-destructive">Couldn&apos;t build a ride right now.</p>
            <button type="button" onClick={build.shuffle} className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground">
              Try again
            </button>
          </div>
        )}

        {build.status === 'loading' && entries.length === 0 && <Building name={first} />}

        {entries.length > 0 && (
          <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="flex min-w-0 flex-col gap-5">
              <AdventureDial value={build.adventure} onChange={build.setAdventure} name={first} regenerating={build.regenerating} />

              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-semibold tracking-tight">The setlist</h2>
                <button
                  type="button"
                  onClick={() => {
                    setRolls((n) => n + 1)
                    build.shuffle()
                  }}
                  className="group flex items-center gap-2 rounded-full border border-border bg-card/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
                >
                  <motion.span key={rolls} animate={{ rotate: rolls ? [0, 360, 720] : 0 }} transition={{ duration: 0.7, ease: EASE }} className="flex">
                    <Dices className="size-4" />
                  </motion.span>
                  Shuffle unlocked
                </button>
              </div>

              <Reorder.Group axis="y" values={entries} onReorder={build.reorder} className="flex flex-col gap-3">
                {entries.map((entry, index) => (
                  <SlotCard
                    key={entry.id}
                    entry={entry}
                    index={index}
                    count={entries.length}
                    frame={frame[index]}
                    queue={queue}
                    queueIndex={queueIndexFor(entry.id)}
                    name={first}
                    open={openId === entry.id}
                    onToggleOpen={() => setOpenId((current) => (current === entry.id ? null : entry.id))}
                    onLock={() => build.toggleLock(entry.id)}
                    onRemove={() => build.remove(entry.id)}
                    onSwap={(choice) => {
                      build.swap(entry.id, choice)
                      setOpenId(null)
                    }}
                  />
                ))}
              </Reorder.Group>

              {withItems.length < entries.length && (
                <button type="button" onClick={build.shuffle} className="flex items-center justify-center gap-2 self-center text-sm text-muted-foreground hover:text-foreground">
                  <RotateCcw className="size-4" /> Fill the empty slots
                </button>
              )}
            </div>

            <RideSummary
              entries={entries}
              frame={frame}
              name={first}
              minutes={params.minutes}
              auditioning={auditioning}
              onAudition={() => playQueue(queue, 0)}
              onStop={close}
              onCopySetlist={setlist}
              onCopyLink={link}
            />
          </div>
        )}
      </section>
    </div>
  )
}
